import { useState } from 'react';
import { Modal, Form, Button, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { DEFAULT_PRESET_NAME } from './presets/defaultPreset';

const MAX_NAME_LEN = 40;

function validateName(name, existingNames, originalName) {
  const trimmed = name.trim();
  if (!trimmed) return 'Name is required.';
  if (trimmed.length > MAX_NAME_LEN) return `Name must be ${MAX_NAME_LEN} characters or fewer.`;
  if (trimmed.toLowerCase() === DEFAULT_PRESET_NAME.toLowerCase()) {
    return `"${DEFAULT_PRESET_NAME}" is a reserved name.`;
  }
  const collision = existingNames.some(
    (n) => n !== originalName && n.toLowerCase() === trimmed.toLowerCase()
  );
  if (collision) return 'A preset with that name already exists.';
  return null;
}

export function ManagePresetsModal({
  show,
  onHide,
  userPresets,
  existingNames,
  onRename,
  onDelete,
}) {
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingError, setEditingError] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [rowError, setRowError] = useState(null);

  const startEdit = (preset) => {
    setEditingId(preset.id);
    setEditingName(preset.name);
    setEditingError(null);
    setPendingDeleteId(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingError(null);
  };

  const commitEdit = async (preset) => {
    const err = validateName(editingName, existingNames, preset.name);
    if (err) {
      setEditingError(err);
      return;
    }
    if (editingName.trim() === preset.name) {
      cancelEdit();
      return;
    }
    try {
      await onRename(preset.id, editingName.trim());
      cancelEdit();
    } catch (e) {
      setEditingError(e?.message || 'Rename failed.');
    }
  };

  const confirmDelete = async (preset) => {
    try {
      await onDelete(preset.id);
      setPendingDeleteId(null);
    } catch (e) {
      setRowError(e?.message || 'Delete failed.');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Presets</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {rowError && <Alert variant="danger">{rowError}</Alert>}

        {userPresets.length === 0 && (
          <p className="text-muted mb-0">No saved presets.</p>
        )}

        {userPresets.length > 0 && (
          <Table size="sm" striped hover>
            <thead>
              <tr>
                <th>Name</th>
                <th style={{ width: 220 }}>Last updated</th>
                <th style={{ width: 160 }} className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {userPresets.map((p) => {
                const isEditing = editingId === p.id;
                const isDeleting = pendingDeleteId === p.id;
                return (
                  <tr key={p.id}>
                    <td>
                      {isEditing ? (
                        <>
                          <Form.Control
                            size="sm"
                            value={editingName}
                            maxLength={MAX_NAME_LEN}
                            onChange={(e) => setEditingName(e.target.value)}
                            isInvalid={!!editingError}
                            autoFocus
                          />
                          {editingError && (
                            <div className="text-danger" style={{ fontSize: '0.8rem' }}>
                              {editingError}
                            </div>
                          )}
                        </>
                      ) : (
                        p.name
                      )}
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {p.updatedAt
                        ? new Date(p.updatedAt).toLocaleString()
                        : '—'}
                    </td>
                    <td className="text-end">
                      {isEditing ? (
                        <>
                          <Button
                            size="sm"
                            variant="success"
                            className="me-1"
                            onClick={() => commitEdit(p)}
                            aria-label="Save rename"
                          >
                            <FontAwesomeIcon icon={faCheck} />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={cancelEdit}
                            aria-label="Cancel rename"
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </Button>
                        </>
                      ) : isDeleting ? (
                        <>
                          <Button
                            size="sm"
                            variant="danger"
                            className="me-1"
                            onClick={() => confirmDelete(p)}
                          >
                            Confirm
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setPendingDeleteId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            className="me-1"
                            onClick={() => startEdit(p)}
                            aria-label="Rename preset"
                          >
                            <FontAwesomeIcon icon={faPen} />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => {
                              setPendingDeleteId(p.id);
                              setRowError(null);
                            }}
                            aria-label="Delete preset"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
